import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { MdSecurity, MdBrush, MdToken } from 'react-icons/md';
import { toast } from 'react-toastify';
import PageContainer from '../../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../../shared/layout/titles/PageTitle';
import PageContent from '../../../../shared/layout/containers/PageContent';
import LoadingMessage from '../../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../../shared/layout/feedback/ErrorMessage';
import {
    fetchManufacturerSettings,
    updateManufacturerSettings,
    regenerateManufacturerApiToken,
} from '../../../../services/Http/ManufacturerApi';

const emptyContact = { name: '', email: '', phone: '' };

const ManufacturerSettings = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        escalation_policy: '',
        sla_target_hours: '',
        escalation_contacts: [emptyContact],
        branding_primary_color: '',
        branding_secondary_color: '',
        branding_logo_url: '',
        support_email: '',
        support_phone: '',
        is_api_active: true,
    });
    const [apiKey, setApiKey] = useState('');
    const [lastSecret, setLastSecret] = useState(null);

    const loadSettings = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const response = await fetchManufacturerSettings();
            setApiKey(response.api?.key || '');
            setForm({
                escalation_policy: response.escalation?.policy || '',
                sla_target_hours: response.escalation?.sla_target_hours || '',
                escalation_contacts:
                    (response.escalation?.contacts && response.escalation.contacts.length
                        ? response.escalation.contacts
                        : [emptyContact]),
                branding_primary_color: response.branding?.primary_color || '',
                branding_secondary_color: response.branding?.secondary_color || '',
                branding_logo_url: response.branding?.logo_url || '',
                support_email: response.organization?.support_email || '',
                support_phone: response.organization?.support_phone || '',
                is_api_active: Boolean(response.api?.is_active),
            });
            setLastSecret(null);
        } catch (err) {
            console.error(err);
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleContactChange = (index, field, value) => {
        setForm((prev) => {
            const contacts = [...prev.escalation_contacts];
            contacts[index] = { ...contacts[index], [field]: value };
            return { ...prev, escalation_contacts: contacts };
        });
    };

    const addContact = () => {
        setForm((prev) => ({
            ...prev,
            escalation_contacts: [...prev.escalation_contacts, emptyContact],
        }));
    };

    const removeContact = (index) => {
        setForm((prev) => {
            const contacts = prev.escalation_contacts.filter((_, idx) => idx !== index);
            return { ...prev, escalation_contacts: contacts.length ? contacts : [emptyContact] };
        });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
            await updateManufacturerSettings(form);
            toast.success(t('ManufacturerSettings.toast.saved'));
            await loadSettings();
        } catch (err) {
            console.error(err);
            toast.error(t('ManufacturerSettings.toast.error'));
        } finally {
            setSaving(false);
        }
    };

    const handleRegenerateToken = async () => {
        try {
            const token = await regenerateManufacturerApiToken();
            setApiKey(token.api_key);
            setLastSecret(token.api_secret);
            toast.success(t('ManufacturerSettings.toast.token'));
        } catch (err) {
            console.error(err);
            toast.error(t('ManufacturerSettings.toast.error'));
        }
    };

    if (loading) {
        return <LoadingMessage>{t('ManufacturerSettings.loading')}</LoadingMessage>;
    }

    if (error) {
        return <ErrorMessage>{t('ManufacturerSettings.error')}</ErrorMessage>;
    }

    return (
        <PageContainer>
            <PageHeader>
                <TitleGroup>
                    <PageTitle>{t('ManufacturerSettings.title')}</PageTitle>
                    <Subtitle>{t('ManufacturerSettings.subtitle')}</Subtitle>
                </TitleGroup>
            </PageHeader>
            <PageContent>
                <form onSubmit={handleSubmit}>
                    <Section>
                        <SectionHeader>
                            <MdSecurity />
                            <div>
                                <SectionTitle>{t('ManufacturerSettings.escalation.title')}</SectionTitle>
                                <SectionSubtitle>{t('ManufacturerSettings.escalation.subtitle')}</SectionSubtitle>
                            </div>
                        </SectionHeader>
                        <FieldGroup>
                            <label>{t('ManufacturerSettings.escalation.policy')}</label>
                            <textarea
                                value={form.escalation_policy}
                                onChange={(event) => handleChange('escalation_policy', event.target.value)}
                                rows={4}
                            />
                        </FieldGroup>
                        <FieldGroup>
                            <label>{t('ManufacturerSettings.escalation.sla')}</label>
                            <input
                                type="number"
                                min="1"
                                max="720"
                                value={form.sla_target_hours || ''}
                                onChange={(event) => handleChange('sla_target_hours', event.target.value)}
                                placeholder={t('ManufacturerSettings.escalation.slaPlaceholder')}
                            />
                        </FieldGroup>
                        <ContactsContainer>
                            <ContactsHeader>
                                <strong>{t('ManufacturerSettings.escalation.contacts')}</strong>
                                <button type="button" onClick={addContact}>
                                    {t('ManufacturerSettings.actions.addContact')}
                                </button>
                            </ContactsHeader>
                            {form.escalation_contacts.map((contact, index) => (
                                <ContactRow key={index}>
                                    <input
                                        type="text"
                                        placeholder={t('ManufacturerSettings.escalation.contactName')}
                                        value={contact.name}
                                        onChange={(event) => handleContactChange(index, 'name', event.target.value)}
                                    />
                                    <input
                                        type="email"
                                        placeholder={t('ManufacturerSettings.escalation.contactEmail')}
                                        value={contact.email}
                                        onChange={(event) => handleContactChange(index, 'email', event.target.value)}
                                    />
                                    <input
                                        type="text"
                                        placeholder={t('ManufacturerSettings.escalation.contactPhone')}
                                        value={contact.phone}
                                        onChange={(event) => handleContactChange(index, 'phone', event.target.value)}
                                    />
                                    <RemoveButton type="button" onClick={() => removeContact(index)}>
                                        {t('ManufacturerSettings.actions.remove')}
                                    </RemoveButton>
                                </ContactRow>
                            ))}
                        </ContactsContainer>
                    </Section>

                    <Section>
                        <SectionHeader>
                            <MdBrush />
                            <div>
                                <SectionTitle>{t('ManufacturerSettings.branding.title')}</SectionTitle>
                                <SectionSubtitle>{t('ManufacturerSettings.branding.subtitle')}</SectionSubtitle>
                            </div>
                        </SectionHeader>
                        <ColorRow>
                            <FieldGroup>
                                <label>{t('ManufacturerSettings.branding.primary')}</label>
                                <input
                                    type="text"
                                    value={form.branding_primary_color}
                                    onChange={(event) => handleChange('branding_primary_color', event.target.value)}
                                    placeholder="#821692"
                                />
                            </FieldGroup>
                            <FieldGroup>
                                <label>{t('ManufacturerSettings.branding.secondary')}</label>
                                <input
                                    type="text"
                                    value={form.branding_secondary_color}
                                    onChange={(event) => handleChange('branding_secondary_color', event.target.value)}
                                    placeholder="#0DCAF0"
                                />
                            </FieldGroup>
                        </ColorRow>
                        <FieldGroup>
                            <label>{t('ManufacturerSettings.branding.logo')}</label>
                            <input
                                type="text"
                                value={form.branding_logo_url}
                                onChange={(event) => handleChange('branding_logo_url', event.target.value)}
                                placeholder="https://cdn.example.com/logo.png"
                            />
                        </FieldGroup>
                        <ColorRow>
                            <FieldGroup>
                                <label>{t('ManufacturerSettings.branding.supportEmail')}</label>
                                <input
                                    type="email"
                                    value={form.support_email}
                                    onChange={(event) => handleChange('support_email', event.target.value)}
                                />
                            </FieldGroup>
                            <FieldGroup>
                                <label>{t('ManufacturerSettings.branding.supportPhone')}</label>
                                <input
                                    type="text"
                                    value={form.support_phone}
                                    onChange={(event) => handleChange('support_phone', event.target.value)}
                                />
                            </FieldGroup>
                        </ColorRow>
                    </Section>

                    <Section>
                        <SectionHeader>
                            <MdToken />
                            <div>
                                <SectionTitle>{t('ManufacturerSettings.api.title')}</SectionTitle>
                                <SectionSubtitle>{t('ManufacturerSettings.api.subtitle')}</SectionSubtitle>
                            </div>
                        </SectionHeader>
                        <FieldGroup>
                            <label>{t('ManufacturerSettings.api.key')}</label>
                            <code>{apiKey || t('ManufacturerSettings.api.none')}</code>
                        </FieldGroup>
                        {lastSecret && (
                            <FieldGroup>
                                <label>{t('ManufacturerSettings.api.secret')}</label>
                                <SecretBox>{lastSecret}</SecretBox>
                            </FieldGroup>
                        )}
                        <ToggleRow>
                            <label>{t('ManufacturerSettings.api.active')}</label>
                            <input
                                type="checkbox"
                                checked={form.is_api_active}
                                onChange={(event) => handleChange('is_api_active', event.target.checked)}
                            />
                        </ToggleRow>
                        <button type="button" onClick={handleRegenerateToken}>
                            {t('ManufacturerSettings.actions.rotateToken')}
                        </button>
                    </Section>

                    <Actions>
                        <button type="submit" disabled={saving}>
                            {saving ? t('ManufacturerSettings.actions.saving') : t('ManufacturerSettings.actions.save')}
                        </button>
                    </Actions>
                </form>
            </PageContent>
        </PageContainer>
    );
};

export default ManufacturerSettings;

const TitleGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const Subtitle = styled.p`
    margin: 0;
    color: ${({ theme }) => theme.colors.textSecondary || '#666'};
`;

const Section = styled.section`
    border: 1px solid rgba(130, 22, 146, 0.15);
    border-radius: 12px;
    padding: 1.25rem;
    margin-bottom: 1.5rem;
    background: #fff;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1rem;

    svg {
        font-size: 1.6rem;
        color: rgba(130, 22, 146, 1);
    }
`;

const SectionTitle = styled.h3`
    margin: 0;
`;

const SectionSubtitle = styled.p`
    margin: 0;
    color: ${({ theme }) => theme.colors.textSecondary || '#666'};
`;

const FieldGroup = styled.div`
    display: flex;
    flex-direction: column;
    margin-bottom: 1rem;

    label {
        font-weight: 600;
        margin-bottom: 0.25rem;
    }

    input,
    textarea {
        border-radius: 8px;
        border: 1px solid rgba(130, 22, 146, 0.2);
        padding: 0.625rem;
        font-size: 0.95rem;
        width: 100%;
    }
`;

const ContactsContainer = styled.div`
    border: 1px dashed rgba(130, 22, 146, 0.3);
    border-radius: 12px;
    padding: 0.75rem;
`;

const ContactsHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;

    button {
        border: none;
        background: rgba(130, 22, 146, 0.1);
        padding: 0.375rem 0.75rem;
        border-radius: 8px;
        cursor: pointer;
        font-weight: 600;
        color: rgba(130, 22, 146, 1);
    }
`;

const ContactRow = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)) auto;
    gap: 0.75rem;
    margin-bottom: 0.625rem;
`;

const RemoveButton = styled.button`
    border: 1px solid rgba(130, 22, 146, 0.3);
    background: transparent;
    border-radius: 6px;
    padding: 0.5rem 0.75rem;
    cursor: pointer;
`;

const ColorRow = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 1rem;
`;

const ToggleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1rem;

    input[type='checkbox'] {
        width: 20px;
        height: 20px;
    }
`;

const SecretBox = styled.code`
    display: block;
    background: rgba(130, 22, 146, 0.1);
    padding: 0.625rem;
    border-radius: 8px;
    word-break: break-all;
`;

const Actions = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 0.75rem;

    button[type='submit'] {
        background: rgba(130, 22, 146, 1);
        color: #fff;
        border: none;
        padding: 0.75rem 1.25rem;
        border-radius: 8px;
        cursor: pointer;
        font-weight: 600;
    }

    button[disabled] {
        opacity: 0.6;
    }
`;
